// src/oura/merchant/SetupNeeded.tsx
//
// The note across the top of every merchant page while their setup is
// unfinished (Jack, 21 Oct 2026): parts of our work are waiting on it, and the
// quiet "Finish your setup" nudge on Home was easy to leave for later. It was
// an orange alert; it read as something gone wrong, so it is now a calm card
// that says what finishing opens up.
//
// It says only what is true of what Oura does. Missing access never holds up
// the results and plan; it holds up the work on what we cannot reach, so the
// sentence names those parts and nothing more. The first AI check can run
// before the answers are in (Jack, 21 Oct 2026), so nothing here says it waits.
//
// Shown while something is the merchant's to finish, under the same count as
// Setup and the To do badge, so it leaves by itself and has nothing to
// dismiss. Not on the Setup tab, which is where its button goes.

import { useLocation } from "react-router-dom";
import { ArrowRightIcon, KeyRoundIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { PillLink, TYPE } from "@oura/ui";
import { ouraHref } from "@oura/nav";
import { setupPart, useMySetup } from "../hooks/useMySetup";
import { MINUTES, type MerchantStepKey } from "../onboarding/oneSetup";

const SETUP_HREF = `${ouraHref("decisions", { role: "oura-client" })}?tab=setup`;

/** What each missing access step holds up, as the sentence names it. */
const HELD: Record<Exclude<MerchantStepKey, "form">, string> = {
    website: "your website",
    gbp: "your Google Business Profile",
    google: "your Google search numbers",
};

/**
 * The next step, said as the thing to do, and its button. Setup used to be
 * said three ways on Home ("a few setup steps left", "3 of 4 done", "Setup: 1
 * thing left") and none named the step (audit, 24 Oct 2026).
 */
const NEXT: Record<MerchantStepKey, { todo: string; button: string }> = {
    form: { todo: "answer a few questions about your business", button: "Answer the questions" },
    gbp: { todo: "give us access to your Google Business Profile", button: "Give profile access" },
    website: { todo: "give us access to your website", button: "Give website access" },
    google: { todo: "connect Search Console and Analytics", button: "Connect Google" },
};

/** "a", "a and b", "a, b and c". */
function listOf(parts: string[]): string {
    return parts.length < 2 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

export function SetupNeeded() {
    const { pathname, search } = useLocation();
    const read = useMySetup();
    const setup = setupPart(read);
    const onSetup = /\/decisions\/?$/.test(pathname) && new URLSearchParams(search).get("tab") === "setup";
    // Home's blue press is the "Needs you" card's job (audit, 24 Oct 2026); here the setup button is white.
    const onHome = /\/dashboard\/?$/.test(pathname);
    if (onSetup || !setup.known || setup.mine === 0) return null;

    const left = read.status === "ready" ? read.setup.left ?? null : null;
    const held = (left ?? []).flatMap((s) => (s.key === "form" ? [] : [HELD[s.key]]));

    // One line, and not the steps again: Setup and Home's "Your setup" list them.
    // Said as what opens up next, not as what is blocked: a merchant who has
    // just signed should read an invitation, not a warning (21 Oct feedback).
    const why = held.length > 0
        ? `Once we have access, we can start work on ${listOf(held)}.`
        : left?.some((s) => s.key === "form")
            ? "A few answers about your business help us get started."
            : // An older read names no steps: say only what the count says.
              `${setup.mine === 1 ? "1 step is" : `${setup.mine} steps are`} left for you.`;
    const started = setup.done > 0;
    // One sentence that names the next step and how long it takes; no count beside it.
    const next = left?.[0] ? NEXT[left[0].key] : null;
    const minutes = left?.[0] ? MINUTES[left[0].key] : null;
    const title = next
        ? `${setup.mine === 1 ? "One setup step left" : `${setup.mine} setup steps left`}: ${next.todo}`
        : started ? "Almost there: a few setup steps left" : "Let's finish setting you up";

    // A calm card, not an alert: blue like every other next step on the
    // merchant's screens, no accent stripe and no warning icon or colour.
    return (
        <section
            aria-labelledby="setup-needed-title"
            className="mb-5 rounded-2xl bg-blue-50/60 ring-1 ring-inset ring-blue-100"
        >
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3.5 sm:px-5">
                <span aria-hidden className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-blue-600 ring-1 ring-inset ring-blue-100 max-sm:hidden">
                    <KeyRoundIcon size={16} />
                </span>
                <div className="min-w-0 flex-1 basis-64">
                    <h2 id="setup-needed-title" className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>
                        {title}
                        {minutes && <span className="ml-2 font-normal tabular-nums text-gray-500">About {minutes} min</span>}
                    </h2>
                    <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-600")}>{why}</p>
                </div>
                <PillLink variant={onHome ? "secondary" : "primary"} to={SETUP_HREF} className="shrink-0 max-sm:w-full max-sm:justify-center">
                    {next?.button ?? (started ? "Continue setup" : "Start setup")}
                    <ArrowRightIcon aria-hidden size={14} />
                </PillLink>
            </div>
        </section>
    );
}
